import { FC, useEffect, useState } from "react";
import CustomPopup from "../../../../custom-components/CustomPopup";
import { getWorkUserProfileImage } from "../../../../slices/works-slice";
import RatingStarSVG from "../../svg/RatingStarIconSVG";
import StarsRating from "./StarsRating";
import { NotificationDTO } from "../../../../domain/models/dto/NotificationDTO";
import CreateReviewSuccess from "./CreateReviewSuccess";
import DisabledTextArea from "../../../../custom-components/form-inputs/DisabledTextArea";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import { NotificationServiceImpl } from "../../../../services/NotificationServiceImpl";
import { DEFAULT_OFFSET, DEFAULT_PAGE, RECORDS_PER_PAGE } from "../../../../domain/constants";
import { setReadCollectionPage, setUnReadCollectionPage } from "../../../../slices/notifications-slice";
import { useTranslation } from "react-i18next";
import { getUserFullName } from "../../../../slices/user-slice";

interface ReviewPopupProps {
    isVisible: boolean;
    setVisible: (value: boolean) => void;
    notification: NotificationDTO;
}

const ReviewPopup: FC<ReviewPopupProps> = (props) => {
    const [isModalVisible, setModalVisible] = useState(false);

    const dispatch = useAppDispatch();

    const { t } = useTranslation();
    const fullName = getUserFullName(props.notification.review.reviewer, t);

    useEffect(() => {
        if (!props.isVisible || props.notification.read) {
            return;
        }

        return () => {
            (async () => {
                const read = await dispatch(NotificationServiceImpl.markAsReadNotificationThunk(props.notification));
                if (read.meta.requestStatus !== "fulfilled") {
                    return;
                }

                // TODO :: change redux state instead needless API's requests
                const result = await Promise.all([
                    dispatch(
                        NotificationServiceImpl.getNotificationsThunk({
                            page: DEFAULT_PAGE,
                            limit: RECORDS_PER_PAGE,
                            offset: DEFAULT_OFFSET,
                            read: true,
                        }),
                    ),
                    dispatch(
                        NotificationServiceImpl.getNotificationsThunk({
                            page: DEFAULT_PAGE,
                            limit: RECORDS_PER_PAGE,
                            offset: DEFAULT_OFFSET,
                            read: false,
                        }),
                    ),
                ]);

                if (result.some((item) => item.meta.requestStatus !== "fulfilled")) {
                    return;
                }

                dispatch(setReadCollectionPage(DEFAULT_PAGE));
                dispatch(setUnReadCollectionPage(DEFAULT_PAGE));
            })();
        };
    }, [props.isVisible]);

    return (
        <>
            <CreateReviewSuccess isVisible={isModalVisible} setIsVisible={setModalVisible} />
            <CustomPopup
                isVisible={props.isVisible}
                setIsVisible={props.setVisible}
                wrapClassName="border border-backgroundButton dmin2:w-[1062px]"
            >
                <div className="flex flex-col items-center w-full bg-backgroundBlock rounded-lg">
                    <div className="flex flex-col items-center">
                        <h2 className="H24B dmax2:H20B text-textWhite text-center mb-8 dmax2:mb-7 dmin2:w-[842px] di6:w-[690px] td5:w-[550px] ti7:w-[420px] tmax4:w-[328px] [word-break:break-word]">
                            {props.notification.review.work.title}
                        </h2>
                        <div className="flex items-center">
                            <div className="min-w-[70px] max-w-[70px] dmax2:min-w-[56px] dmax2:max-w-[56px] h-[70px] min-h-[70px] max-h-[70px] dmax2:h-[56px] dmax2:min-h-[56px] dmax2:max-h-[56px] mr-4 dmax2:mr-3">
                                <img
                                    src={getWorkUserProfileImage(props.notification.review.reviewer, true)}
                                    alt="user-photo"
                                    className="rounded-full cursor-pointer object-cover w-full h-full"
                                />
                            </div>
                            <div className="flex flex-col items-start">
                                <p className="H20B dmax2:H16B text-textWhite line-clamp-2">{fullName}</p>
                                <div className="flex items-center">
                                    <span className="H16 text-textWhite mr-[2px]">
                                        {props.notification.review.reviewer?.avgRating}
                                    </span>
                                    <RatingStarSVG className="w-5 mr-1" />
                                    <span className="H16 text-textFaded">
                                        {props.notification.review.reviewer?.reviewsCount}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-center py-[24px]">
                            <StarsRating rate={props.notification.review.rate} wrapClassName="pb-[16px]" />
                        </div>
                    </div>
                    <div className="relative flex flex-col items-center dmin2:w-[842px] di6:w-[690px] td5:w-[550px] tmax4:w-[328px]">
                        <div className="relative flex flex-col justify-start w-full">
                            <DisabledTextArea
                                restrainerClassName="!h-[200px] !pb-[24px]"
                                value={props.notification.review.comment}
                            />
                        </div>
                    </div>
                </div>
            </CustomPopup>
        </>
    );
};

export default ReviewPopup;
